import { Page, Text, View, Document, StyleSheet, Image, Link, } from "@react-pdf/renderer";
import { References } from "../../constant/references";
import { useEffect, useState } from "react";

const styles = StyleSheet.create({
  page: { padding: 40 },
  section: { marginTop: 8 },
  sectionText: {
    fontSize: 9,
    fontWeight: "normal",
    color: "#161616",
    lineHeight: 1.4,
    textAlign: "justify",
    marginTop: 4,
  },
  sectionHeading: { fontSize: 12, color: "#000000", fontWeight: "bold" },
  headingSection: {
    marginBottom: 10,
  },
  heading: {
    fontSize: 14,
    color: "#000000",
    fontWeight: "bold",
  },
  projectName: {
    fontSize: 10,
    fontWeight: "normal",
    color: "#161616",
  },
  image: { width: "100%", height: 300, objectFit: "contain" },
  subheading: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 3,
    marginTop: 5,
  },
  imageTitle: {
    textAlign: "center",
    fontSize: 10,
    fontWeight: "normal",
    color: "#585858",
    marginBottom: 10,
  },
  mrnaSection: { marginTop: 3 },
  mrnaCentre: { marginLeft: 40 },
  networkParameter: {
    fontSize: 9,
    color: "#333",
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 4,
  },
  networkParameterTitle: {
    fontWeight: "bold",
  },
});

// Source content generator
const sourceContent = {
  TCGA: {
    mRNA: (type) =>
      `TCGA ${type} mRNA expression unstranded FPKM-UQ (Fragments Per Kilobase of transcript per Million mapped reads Upper Quartile) data were obtained from GDC Data Portal (Heath et al., 2021).`,
    Protein: (type) =>
      `TCGA ${type} protein expression data were sourced from cBioportal (de Bruijn et al., 2023; Gao et al., 2013; Cerami et al., 2012).`,
    CNV: (type) =>
      `TCGA ${type} CNV (copy number variation) data were obtained from GDC Data Portal (Heath et al., 2021). In the original data, 1 indicates a deletion or loss of one copy, 2 indicates the normal diploid state (two copies), and 3 indicates a duplication or gain of one copy. For the network generation, the CNV data were categorized into 3 levels: 1 (amplification – 2 in the original data), -1 (deletion – 1 in the original data), and 0 (normal – 2 in the original data).`,
    Methylation: (type) => (
      <>
        <Text style={styles.sectionText}>
          TCGA {type} DNA methylation data were obtained from GDC Data Portal
          (Heath et al., 2021). In the original data, methylation level is
          represented by Beta values between 0 and 1, representing the
          proportion of methylation. We further categorized the methylation
          level as:
        </Text>
        <Text style={styles.sectionText}>
          • Values {"<"} 0.2 → –1 (Hypomethylated)
        </Text>
        <Text style={styles.sectionText}>
          • Values {">"} 0.8 → 1 (Hypermethylated)
        </Text>
        <Text style={styles.sectionText}>
          • Values in between → 0 (Heterogeneously Methylated)
        </Text>
      </>
    ),
    Fusion: (type) =>
      `TCGA ${type} gene fusion data were obtained from GDC Data Portal (Heath et al., 2021). Gene fusion events were processed as binary data: 1 (fusion present), 0 (fusion absent).`,
    Mutation: (type) =>
      `TCGA ${type} mutation data were obtained from GDC Data Portal (Heath et al., 2021). Mutations were processed as binary data: 1 (mutation present), 0 (mutation absent).`,
  },
  CCLE: {
    mRNA: (type) =>
      `CCLE ${type}  cell line RNA-seq data were obtained from DepMap 24Q4 (https://depmap.org/portal/data_page, accessed on 24 April 2025) (Broad, 2024).`,
    Protein: (type) =>
      `CCLE ${type} cell line protein data were obtained from cBioportal (de Bruijn et al., 2023; Gao et al., 2013; Cerami et al., 2012; Ghandi et al., 2019; Nusinow et al., 2020). (https://www.cbioportal.org/datasets, accessed on 24 April 2025) .`,
    CNV: (type) => (
      <>
        <Text style={styles.sectionText}>
          CCLE {type} cell line copy number data were obtained from DepMap 24Q4
          (https://depmap.org/portal/data_page, accessed on 24 April 2025)
          (Broad, 2024). In the original data, the CNV values are log2(1 + copy
          number/2), the categorization of the CNV values is:
        </Text>
        <Text style={styles.sectionText}>• {"<"} 0.8 → –1 (Deletion)</Text>
        <Text style={styles.sectionText}>• 0.8 - 1.2 → 0 (Normal)</Text>
        <Text style={styles.sectionText}>• {">"} 1.2 → 1 (Amplification)</Text>
      </>
    ),
    Methylation: (type) => (
      <>
        <Text style={styles.sectionText}>
          The CCLE {type} cell line copy number data were obtained from
          Methylation (RRBS) (https://depmap.org/portal/data_page, accessed on
          24 April 2025). In the original data, methylation level is represented
          by Beta values between 0 and 1, representing the proportion of
          methylation. We further categorized the methylation level as:
        </Text>
        <Text style={styles.sectionText}>
          • Values {"<"} 0.2 → –1 (Hypomethylated)
        </Text>
        <Text style={styles.sectionText}>
          • Values {">"} 0.8 → 1 (Hypermethylated)
        </Text>
        <Text style={styles.sectionText}>
          • Values in between → 0 (Heterogeneously Methylated)
        </Text>
      </>
    ),
    Fusion: (type) =>
      `CCLE ${type}  cell line gene fusion data were obtained from DepMap 24Q4 (https://depmap.org/portal/data_page, accessed on 24 April 2025) (Broad, 2024). Gene fusion events were processed as binary data: 1 (fusion present), 0 (fusion absent).`,
    Mutation: (type) =>
      `The CCLE ${type} cell line mutation data were obtained from DepMap 24Q4 (https://depmap.org/portal/data_page, accessed on 24 April 2025) (Broad, 2024). Mutation events were processed as binary data: 1 (mutation present), 0 (mutation absent).`,
  },
};

const commonText =
  "To define the level of gene expression, 27 housekeeping genes (ACTB, B2M, CDKN1B, ESD, FLOT2, GAPDH, GRB2, GUSB, HMBS, HPRT1, HSP90AB1, IPO8, LDHA, NONO, PGK1, POLR2A, PPIA, PPIH, PPP1CA, RHOA, RPL13A, SDCBP, TBP, TFRC, UBC, YAP1, YWHAZ) (N. L. Guo et al., 2018; Zhan et al., 2014; Walter et al., 2016; Saviozzi et al., 2006; Chang et al., 2018) were used to define the thresholds of gene expression categorization. The categorization ensured that 30% of samples were either over- or under-expressed, and the number n which meets the following Equation (1) was computed for each gene:";

const booleanImplication =
  "The Boolean implication network algorithm used to generate the gene association networks in this study was developed by Guo et al. (L. Guo, Cukic, & Singh, 2003; N. L. Guo et al., 2011; N. L. Guo & Wan, 2012). A detailed description of the algorithm and its applications in multi-omics network modeling is available in previous publications (Ye, Singh, Qian, & Guo, 2021; Ye, Falatovich, et al., 2021). Implication rules representing gene association relationships were evaluated using scope and precision metrics as defined by the algorithm. Thresholds for scope and precision were calculated using a one-tailed z-score test at a specified significance level and sample size. Only implication rules passing both scope and precision thresholds were retained. For gene pairs with multiple passing rules, the rule with the highest precision was selected.";

const previousDataFields = [
  { label: "Cancer Type", key: "cancer_type" },
  { label: "Subtype", key: "histological_subtype" },
  { label: "Normal/Tumor", key: "normal_tumor" },
  { label: "Source Gene", key: "source_gene" },
  { label: "Target Gene", key: "target_gene" },
  { label: "Data Source", key: "data_source" },
  { label: "Sample Type", key: "sample_type" },
  { label: "Risk Level", key: "risk_level" },
  { label: "Therapeutic Response", key: "therapeutic_response" },
  { label: "Subsampling", key: "subsampling_yes_no" },
  { label: "Significances Level", key: "p_value" },
  { label: "Scope Threshold", key: "scope" },
  { label: "Precision Threshold", key: "precision" },
];
const PDFDocument = ({ networkInformation, fileContent, graphImage, fileUrl }) => {
  const [citedReferences, setCitedReferences] = useState([]);
  const geneTypes = Array.from(
    new Set([
      networkInformation.source_gene_name,
      networkInformation.target_gene_name,
    ])
  );
  const content = sourceContent[networkInformation.data_source];
  useEffect(() => {
    if (geneTypes && content) {
      const uniqueCitations = getCitedReferences();
      setCitedReferences(uniqueCitations);
    }
  }, [geneTypes, content, networkInformation]);

  const getCitedReferences = () => {
    const subtype = networkInformation?.histological_subtype;
    const seenCitations = new Set();
    const result = [];

    const tryAddCitation = (textSource) => {
      if (typeof textSource !== 'string') {
        console.log("TextSource is not a string")
        return;
      }

      References.forEach(({ citation, description, url }) => {
        if (
          citation &&
          textSource.includes(citation) &&
          !seenCitations.has(citation)
        ) {
          seenCitations.add(citation);
          result.push({ citation, description, url });
        }
      });
    };

    if (
      networkInformation.source_gene_name === "mRNA" ||
      networkInformation.target_gene_name === "mRNA" ||
      networkInformation.source_gene_name === "Protein" ||
      networkInformation.source_gene_name === "Protein"
    ) {
      tryAddCitation(commonText);
    }

    // Step 2: Check in booleanImplication
    tryAddCitation(booleanImplication);

    // Step 3: Check in each geneType section content
    geneTypes.forEach((type) => {
      const section = content?.[type];
      if (section && typeof section === "function") {
        const sectionText = section(subtype);
        if (sectionText) {
          tryAddCitation(sectionText);
        }
      }
    });

    return result;
  };





  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headingSection}>
          <Text style={styles.heading}>
            Network Report :{" "}
            <Text style={styles.projectName}>
              {networkInformation.project_name}
            </Text>
          </Text>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Parameters of this Network</Text>
          <View style={styles.previousData}>
            <Text style={styles.networkParameter}>
              {previousDataFields
                .map((item, index) => {
                  const value =
                    networkInformation?.[item.key]?.label ||
                    networkInformation?.[item.key];

                  if (value === undefined || value === null || value === "")
                    return null;

                  return (
                    <Text key={index}>
                      <Text style={styles.networkParameterTitle}>
                        {item.label}:
                      </Text>{" "}
                      {value}
                      {index < previousDataFields.length - 1 ? ", " : ""}
                    </Text>
                  );
                })
                .filter(Boolean)}
            </Text>
          </View>
        </View>
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Materials and Methods</Text>
          <Text style={styles.subheading}>Boolean Implication Networks</Text>
          <Text style={styles.sectionText}>{booleanImplication}</Text>
        </View>

        {/* Data Source Section */}
        <View style={styles.section}>
          <Text style={styles.subheading}>Data Source and Preprocessing</Text>
          {geneTypes.map((type) => {
            const section = content?.[type];
            return section ? (
              <View key={type}>

                {(() => {
                  const content = section(networkInformation.histological_subtype);
                  return typeof content === "string" ? (
                    <Text style={styles.sectionText}>{content}</Text>
                  ) : (
                    content
                  );
                })()}
              </View>
            ) : null;
          })}
        </View>

        {(networkInformation.source_gene_name === "mRNA" ||
          networkInformation.target_gene_name === "mRNA" ||
          networkInformation.source_gene_name === "Protein" ||
          networkInformation.source_gene_name === "Protein") && (
            <View style={styles.mrnaSection}>
              <Text style={styles.sectionText}>{commonText} </Text>
              <View style={styles.mrnaCentre}>
                <Text style={styles.sectionText}>
                  (the number of samples with expression {">"} mean + n × std) +{" "}
                </Text>
                <Text style={styles.sectionText}>
                  (the number of samples with expression {"<"} mean – n × std){" "}
                </Text>
                <Text style={styles.sectionText}>
                  = 30% of the total number of samples (1)
                </Text>
              </View>
              <Text style={styles.sectionText}>
                The sample with a gene expression higher than (mean + n × standard
                deviation) was defined as over-expressed; the sample with a gene
                expression lower than (mean – n × standard deviation) was defined
                as under-expressed; the sample with a gene expression in between
                the two thresholds was defined as normal.
              </Text>
            </View>
          )}
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Results</Text>
          <Text style={styles.subheading}>Network Generation</Text>
          <Text style={styles.sectionText}>{fileContent.imageContent}</Text>
          {graphImage && <Image src={graphImage} style={styles.image} />}
          <Text style={styles.imageTitle}>
            Figure 1.{fileContent.imageTitle}
          </Text>
          <Text style={styles.sectionText}>
            Detailed network information is provided in
          </Text>
          {/* <Text style={styles.sectionText}>{fileContent.file}</Text> */}
          <Text style={styles.sectionText}>
            <Link src={fileUrl} style={{ color: "blue", textDecoration: "none" }}>
              {fileContent.file}
            </Link>
          </Text>


        </View>
        <View style={styles.section}>
          <Text style={styles.subheading}>References</Text>

          {/* Render the citations */}
          {citedReferences.length > 0 && (
            <View style={styles.citations}>
              {citedReferences.map((ref, index) => (
                <Text key={index} style={styles.sectionText}>
                  <Link
                    href={ref.url}
                    isExternal
                    style={{ color: "#434AAD", textDecoration: "none" }}
                  >
                    {ref.description}
                  </Link>
                </Text>
              ))}
            </View>
          )}
        </View>
      </Page>
    </Document>
  );
};

export default PDFDocument;
